<template>
  <main class="physical-transfer-page">
    <AppNavBar :title="t('wallet.physicalTransfer')" />

    <section v-if="!loading && selectedCard" class="transfer-shell">
      <section class="direction-card">
        <div class="direction-column">
          <span>{{ transferMode === 'out' ? t('wallet.transferFrom') : t('wallet.transferTo') }}</span>
          <div data-strong>{{ t('wallet.fundingAccount') }}</div>
        </div>

        <button type="button" class="switch-btn" @click="switchMode">
          <app-image src="@/assets/images/physical_exchange.png" alt="" />
        </button>

        <button type="button" class="direction-column direction-column--card" @click="showCardSheet = true">
          <span>{{ transferMode === 'out' ? t('wallet.transferTo') : t('wallet.transferFrom') }}</span>
          <div data-strong>{{ `${t('card.physicalCard')} (${selectedCard.last4 || '****'})` }}</div>
          <van-icon name="arrow" />
        </button>
      </section>

      <section class="asset-card">
        <h2>{{ t('wallet.exchangeCoin') }}</h2>

        <div class="asset-item">
          <app-image :src="assetIcon" alt="" />
          <div>
            <div data-strong>{{ assetSymbol }}</div>
            <span>{{ t('wallet.availableBalance') }} {{ availableBalance }} {{ assetSymbol }}</span>
          </div>
        </div>

        <div class="price-row">
          <span>{{ t('card.exchangeRate') }}</span>
          <div data-strong>1 {{ displayBaseSymbol }} ≈ {{ displayPrice }} {{ displayQuoteSymbol }}</div>
        </div>

        <div class="amount-header">
          <h3>{{ amountTitle }}</h3>
          <span>({{ t('card.minAmount') }} {{ minAmount }} {{ inputSymbol }})</span>
        </div>

        <van-field v-model="amount" type="number" clearable :placeholder="amountPlaceholder">
          <template #button>
            <span class="amount-symbol">{{ inputSymbol }}</span>
            <van-button size="small" type="primary" @click="fillAll">{{ t('wallet.all') }}</van-button>
          </template>
        </van-field>

        <div class="calc-row">
          <span>{{ arrivalLabel }}</span>
          <div data-strong>{{ arrivalText }}</div>
        </div>
        <div class="calc-row">
          <span>{{ feeLabel }}</span>
          <div data-strong>{{ feeText }}</div>
        </div>
      </section>

      <van-button block round type="primary" class="submit-btn" @click="submitTransfer">
        {{ t('common.confirm') }}
      </van-button>
    </section>

    <section v-else-if="!loading" class="empty-card">
      <AppEmptyState :description="t('card.empty')" />
      <van-button round type="primary" @click="router.push('/wallet/open-card')">
        {{ t('card.applyCard') }}
      </van-button>
    </section>

    <van-skeleton v-else title :row="8" />

    <van-action-sheet
      v-model:show="showCardSheet"
      :actions="cardActions"
      :cancel-text="t('common.cancel')"
      close-on-click-action
      @select="selectCard"
    />
  </main>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { closeToast, showConfirmDialog, showFailToast, showLoadingToast, showToast } from 'vant';
import { showPassedToast } from '@/utils/toast';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { cardMyList, pcCardCharge, pcCardChargeInfo, pcCardWithdraw, pcCardWithdrawInfo } from '@/api/card';
import { normalizeFeeRate } from '@/view/card/utils';
import { useProfileStore } from '@/stores/profile';
import { getDeviceId } from '@/utils/device';
import { formatMoney } from '@/utils/format';
import usdIcon from '@/assets/images/usd.png';
import usdtIcon from '@/assets/images/usdt.png';

const { t } = useI18n();
const router = useRouter();
const profileStore = useProfileStore();

const loading = ref(true);
const cards = ref([]);
const selectedCardIndex = ref(0);
const transferMode = ref('in');
const cardInfo = ref({});
const amount = ref('');
const showCardSheet = ref(false);

const selectedCard = computed(() => cards.value[selectedCardIndex.value] || null);
const isTransferIn = computed(() => transferMode.value === 'in');
const displayBaseSymbol = computed(() => String(isTransferIn.value ? cardInfo.value.balanceSymbol || 'USDT' : selectedCard.value?.balance?.currency || 'USD'));
const displayQuoteSymbol = computed(() => String(cardInfo.value.priceSymbol || (isTransferIn.value ? selectedCard.value?.balance?.currency || 'USD' : 'USDT')));
const displayPrice = computed(() => formatMoney(cardInfo.value.price || 1));
const inputSymbol = computed(() => String(isTransferIn.value ? cardInfo.value.balanceSymbol || 'USDT' : selectedCard.value?.balance?.currency || 'USD'));
const availableBalance = computed(() => String(isTransferIn.value ? cardInfo.value.balance || '0.00' : selectedCard.value?.balance?.available || '0.00'));
const minAmount = computed(() => String(isTransferIn.value ? cardInfo.value.chargeMin || '0' : cardInfo.value.withdrawMin || '0'));
const amountValue = computed(() => Number(amount.value || 0));
const feeValue = computed(() => {
  if (!amountValue.value) return 0;
  const price = Number(cardInfo.value.price || 1);
  if (isTransferIn.value) {
    return amountValue.value * normalizeFeeRate(cardInfo.value.chargeFeeRate);
  }
  return amountValue.value * normalizeFeeRate(cardInfo.value.withdrawFeeRate) * price;
});
const arrivalValue = computed(() => {
  if (!amountValue.value) return 0;
  const price = Number(cardInfo.value.price || 1);
  if (isTransferIn.value) {
    return Math.max(amountValue.value - feeValue.value, 0) * price;
  }
  return Math.max(amountValue.value - feeValue.value, 0);
});
const assetIcon = computed(() => (isTransferIn.value ? usdtIcon : usdIcon));
const assetSymbol = computed(() => (isTransferIn.value ? String(cardInfo.value.balanceSymbol || 'USDT') : String(selectedCard.value?.balance?.currency || 'USD')));
const amountTitle = computed(() => (isTransferIn.value ? t('wallet.transferInAmount') : t('wallet.transferOutAmount')));
const amountPlaceholder = computed(() => (isTransferIn.value ? t('card.enterTransferInAmount') : t('card.enterTransferOutAmount')));
const arrivalLabel = computed(() => (isTransferIn.value ? t('card.expectedCardArrival') : t('card.expectedAccountArrival')));
const feeLabel = computed(() => (isTransferIn.value ? t('wallet.fee') : t('wallet.withdrawFeeLabel')));
const arrivalText = computed(() => `${formatMoney(arrivalValue.value)} ${displayQuoteSymbol.value}`);
const feeText = computed(() => `${formatMoney(feeValue.value)} ${isTransferIn.value ? assetSymbol.value : displayQuoteSymbol.value}`);
const cardActions = computed(() => cards.value.map((item, index) => ({
  name: `${item.bin || '****'}-****-****-${item.last4 || '****'} (${formatMoney(item.balance?.available || 0)} ${item.balance?.currency || 'USD'})`,
  index
})));

function fillAll() {
  amount.value = String(availableBalance.value || '0');
}

function switchMode() {
  transferMode.value = isTransferIn.value ? 'out' : 'in';
  amount.value = '';
  loadCardInfo();
}

function selectCard(action) {
  selectedCardIndex.value = action.index;
  amount.value = '';
  loadCardInfo();
}

async function loadCards() {
  const result = await cardMyList({
    address: profileStore.platformAddress || null,
    pageNum: 1,
    pageSize: 999,
    deviceToken: getDeviceId(),
    cardType: 1
  });
  cards.value = Array.isArray(result?.list) ? result.list : [];
  selectedCardIndex.value = 0;
}

async function loadCardInfo() {
  if (!selectedCard.value?.id || !profileStore.platformAddress) {
    cardInfo.value = {};
    return;
  }

  const result = isTransferIn.value
    ? await pcCardChargeInfo({ address: profileStore.platformAddress, cardId: selectedCard.value.id })
    : await pcCardWithdrawInfo({ address: profileStore.platformAddress, cardId: selectedCard.value.id });
  cardInfo.value = result || {};
}

async function bootstrap() {
  if (!profileStore.platformAddress) {
    router.replace('/login');
    return;
  }

  loading.value = true;
  showLoadingToast({ message: t('common.loading'), forbidClick: true, duration: 0 });
  try {
    await loadCards();
    await loadCardInfo();
  } catch (error) {
    showFailToast(error.msg || t('common.retryLater'));
  } finally {
    loading.value = false;
    closeToast();
  }
}

async function submitTransfer() {
  if (!selectedCard.value?.id) {
    showFailToast(t('wallet.selectCard'));
    return;
  }
  if (!amountValue.value || amountValue.value <= 0) {
    showFailToast(t('card.enterValidAmount'));
    return;
  }
  if (Number(minAmount.value) && amountValue.value < Number(minAmount.value)) {
    showFailToast(`${t('card.minAmount')} ${minAmount.value} ${inputSymbol.value}`);
    return;
  }

  try {
    await showConfirmDialog({
      title: t('wallet.physicalTransfer'),
      message: t('wallet.transferConfirm')
    });
  } catch (error) {
    return;
  }

  showLoadingToast({ message: t('common.submitting'), forbidClick: true, duration: 0 });
  try {
    const payload = {
      address: profileStore.platformAddress,
      amount: amount.value,
      cardId: selectedCard.value.id
    };
    if (isTransferIn.value) {
      await pcCardCharge(payload);
    } else {
      await pcCardWithdraw(payload);
    }
    closeToast();
    showPassedToast(t('card.transferSuccess'));
    amount.value = '';
    await loadCards();
    await loadCardInfo();
  } catch (error) {
    closeToast();
    showFailToast(error.msg || t('common.retryLater'));
  }
}

onMounted(bootstrap);
</script>

<style scoped lang="scss">
.physical-transfer-page {
  min-height: var(--app-viewport-height);
  padding: 15px 14px 34px;
  background: #f7f8fa;
}

.transfer-shell {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.direction-card,
.asset-card {
  border-radius: 10px;
  background: #ffffff;
}

.direction-card {
  position: relative;
  display: grid;
  gap: 10px;
  padding: 0;
}

.direction-column {
  padding: 16px;

  span,
  [data-strong] {
    display: block;
  }

  span {
    color: rgba(0, 0, 0, 0.45);
    font-size: 13px;
  }

  [data-strong] {
    margin-top: 4px;
    color: var(--wp-color-text);
    font-size: 16px;
    font-weight: 500;
  }
}

.direction-column--card {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  border: 0;
  border-top: 1px solid #f2f3f5;
  background: transparent;
  text-align: left;

  .van-icon {
    margin-top: 14px;
    color: #86909c;
  }
}

.switch-btn {
  position: absolute;
  top: 50%;
  left: 50%;
  display: flex;
  width: 56px;
  height: 56px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #ffffff;
  transform: translate(-50%, -50%);

  .app-image {
    width: 32px;
    height: 32px;
  }
}

.asset-card {
  padding: 18px 16px;

  h2,
  h3 {
    margin: 0;
    color: #4e5969;
    font-size: 16px;
    font-weight: 500;
  }
}

.asset-item {
  display: flex;
  align-items: center;
  margin-top: 15px;
  padding: 16px 12px;
  border-radius: 10px;
  background: #ffffff;

  .app-image {
    width: 36px;
    height: 36px;
    margin-right: 10px;
  }

  [data-strong],
  span {
    display: block;
  }

  [data-strong] {
    color: #1d2129;
    font-size: 16px;
    font-weight: 500;
  }

  span {
    margin-top: 4px;
    color: #86909c;
    font-size: 12px;
  }
}

.price-row,
.calc-row,
.amount-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.price-row {
  margin: 8px 0 16px;

  span {
    color: #86909c;
    font-size: 13px;
  }

  [data-strong] {
    color: #1d2129;
    font-size: 13px;
    font-weight: 500;
  }
}

.amount-header {
  align-items: flex-end;

  span {
    color: rgba(0, 0, 0, 0.45);
    font-size: 13px;
  }
}

.amount-symbol {
  margin-right: 8px;
  color: #1d2129;
  font-weight: 500;
}

.calc-row {
  margin-top: 16px;

  span {
    color: rgba(0, 0, 0, 0.65);
    font-size: 14px;
  }

  [data-strong] {
    color: #1d2129;
    font-size: 15px;
    font-weight: 500;
  }
}

.submit-btn {
  margin-top: 12px;
}

.empty-card {
  padding-top: 80px;
}
</style>
